import {
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';

import { NoData } from '@/components/legacy-ui/NoData';
import { Table } from '@/components/table/Table';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useDownloadFile } from '@/hooks/useFiles';
import { useGenericSearch } from '@/hooks/useGenericSearch';
import { useMy } from '@/hooks/useMy';
import {
  useConnectPlexTracReport,
  useDisconnectPlexTracReport,
} from '@/hooks/usePlextrac';
import {
  type ReportItem,
  useExportReport,
  useGetReporting,
} from '@/hooks/useReporting';
import { Configuration, ResourceType } from '@/types';
import { cn } from '@/utils/classname';

import { ActiveExportJobs } from './ActiveExportJobs';
import { useReportingTableColumns } from './hooks/useReportingTableColumns';

interface ReportingSettingsSectionProps {
  className?: string;
  plexTracClientId?: string;
  customerEmail?: string;
}

export function ReportingSettingsSection({
  className,
  plexTracClientId,
  customerEmail,
}: ReportingSettingsSectionProps) {
  const isValidClientId = Boolean(
    plexTracClientId && plexTracClientId.trim() !== ''
  );

  const {
    data: reportingData,
    status: reportingStatus,
    error: reportingError,
  } = useGetReporting({
    enabled: isValidClientId,
    clientId: plexTracClientId,
    customerEmail,
  });

  const { mutateAsync: exportReport } = useExportReport(customerEmail);
  const { mutateAsync: downloadFile } = useDownloadFile(customerEmail);
  const { mutateAsync: connectReport } =
    useConnectPlexTracReport(customerEmail);
  const { mutateAsync: disconnectReport } =
    useDisconnectPlexTracReport(customerEmail);

  const { data: sharedFiles } = useMy(
    { resource: 'file', query: 'reports/' },
    {
      enabled: isValidClientId,
      email: customerEmail,
      refetchInterval: 3000,
    }
  );

  const { data: configurations } = useMy(
    { resource: 'configuration', query: 'plextrac#report' },
    {
      enabled: isValidClientId,
      email: customerEmail,
      refetchInterval: 3000,
    }
  );

  const { data: activeExportJobsData } = useGenericSearch(
    {
      label: ResourceType.Job,
      query: 'source:export-plextrac',
    },
    {
      enabled: isValidClientId,
      email: customerEmail,
      refetchInterval: 3000,
    }
  );

  const activeExportJobs = activeExportJobsData?.jobs || [];

  const isLoading = reportingStatus === 'pending';

  // Track connected reports
  const [connectedReports, setConnectedReports] = useState<Set<string>>(
    new Set()
  );
  const [connectingReportId, setConnectingReportId] = useState<string | null>(
    null
  );

  // Process configurations to extract connected report IDs
  useEffect(() => {
    if (configurations) {
      const connected = new Set<string>();
      configurations.forEach((config: Configuration) => {
        // Extract report ID from key like "#configuration#plextrac#report-936718458"
        const match = config.key?.match(/plextrac#report-(\d+)/);
        if (match) {
          connected.add(match[1]);
        }
      });
      setConnectedReports(connected);
    }
  }, [configurations]);

  const handleExportReport = async (reportId: string) => {
    try {
      await exportReport({ reportId });
    } catch (error) {
      console.error('Failed to export report:', error);
    }
  };

  const handleDownloadReport = async (report: ReportItem) => {
    // Find exported file to download
    if (!sharedFiles || !plexTracClientId) {
      await handleExportReport(report.id);
      return;
    }

    const reportIdStr = String(report.id);
    const pattern = `reports/plextrac-report-${plexTracClientId}-${reportIdStr}-`;
    const exportedFile = sharedFiles.find(file =>
      file.name?.startsWith(pattern)
    );

    if (!exportedFile) {
      await handleExportReport(report.id);
      return;
    }

    try {
      await downloadFile({
        name: exportedFile.name,
        downloadName: `${report.name}.pdf`,
        type: 'application/pdf',
      });
    } catch (error) {
      console.error('Failed to download report:', error);
    }
  };

  const handleConnectReport = async (
    report: ReportItem,
    isConnected: boolean
  ) => {
    setConnectingReportId(report.id);
    try {
      if (isConnected) {
        // Disconnect using new API endpoint
        await disconnectReport({
          reportId: Number(report.id),
        });

        setConnectedReports(prev => {
          const next = new Set(prev);
          next.delete(report.id);
          return next;
        });
      } else {
        // Connect using new API endpoint (stores full ReportInfo)
        await connectReport({
          reportId: Number(report.id),
        });

        setConnectedReports(prev => new Set(prev).add(report.id));
      }
    } catch (error) {
      console.error('Failed to connect/disconnect report:', error);
      // Toast is already handled by the mutation hooks
    } finally {
      setConnectingReportId(null);
    }
  };

  const checkIfReportIsConnected = React.useCallback(
    (reportId: string | number) => {
      return connectedReports.has(String(reportId));
    },
    [connectedReports]
  );

  // Use extracted table columns hook
  const { columns, rowActions } = useReportingTableColumns({
    sharedFiles,
    plexTracClientId,
    connectedReports,
    connectingReportId,
    onExportReport: handleExportReport,
    onDownloadReport: handleDownloadReport,
    onConnectReport: handleConnectReport,
    checkIfReportIsConnected,
  });

  return (
    <div className={cn('flex flex-col gap-6', className)}>
      {isLoading ? (
        <Skeleton className="min-h-[200px]" />
      ) : (
        <div className="bg-chariot-background-secondary overflow-hidden rounded-lg">
          {!isValidClientId && (
            <div className="p-6">
              <div className="border-chariot-stroke-info bg-chariot-status-info/10 flex items-center gap-3 rounded-md border p-4">
                <InformationCircleIcon className="text-chariot-status-info size-5 shrink-0" />
                <div className="flex flex-col gap-1">
                  <span className="text-chariot-text-primary text-sm font-semibold">
                    PlexTrac configuration required
                  </span>
                  <span className="text-chariot-text-secondary text-xs">
                    Please configure your PlexTrac Client ID above to view and
                    export reports
                  </span>
                </div>
              </div>
            </div>
          )}

          {isValidClientId && reportingError && (
            <div className="p-6">
              <div className="border-chariot-stroke-critical bg-chariot-status-critical/10 flex items-center gap-3 rounded-md border p-4">
                <ExclamationTriangleIcon className="text-chariot-status-critical size-5 shrink-0" />
                <div className="flex flex-col gap-1">
                  <span className="text-chariot-text-primary text-sm font-semibold">
                    Unable to load reports
                  </span>
                  <span className="text-chariot-text-secondary text-xs">
                    Please verify your PlexTrac Client ID and ensure you have
                    published reports available
                  </span>
                </div>
              </div>
            </div>
          )}

          {isValidClientId && !reportingError && (
            <>
              {reportingData?.reports && reportingData?.reports?.length > 0 && (
                <>
                  <Table<ReportItem>
                    name="reports"
                    columns={columns}
                    data={reportingData.reports}
                    status={reportingStatus}
                    error={reportingError}
                    rowActions={rowActions}
                    rowHeight={64}
                  />

                  <ActiveExportJobs
                    jobs={activeExportJobs}
                    reports={reportingData?.reports}
                  />
                </>
              )}

              {reportingData &&
                (!reportingData.reports ||
                  reportingData.reports.length === 0) && (
                  <div className="p-8">
                    <NoData
                      title="No Published Reports Available"
                      description="Your PlexTrac client is connected, but no published reports were found in your workspace. Reports must be published in PlexTrac before they can be exported."
                      action={
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            window.open(
                              'https://praetorian.plextrac.com',
                              '_blank',
                              'noopener,noreferrer'
                            )
                          }
                        >
                          Open PlexTrac
                        </Button>
                      }
                    />
                  </div>
                )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
